import { LANGUAGE_CONFIG } from '@/lib/i18n';
import { getPresignedPostUploadErrorMessage, PresignedPostUploadError } from '@/utils/uploadViaPresignedPost';
import { HTTP_STATUS } from './http-status';
export { ErrorResponseSchema, type ErrorResponse } from './error-response-schema';
import { ErrorResponseSchema, type ErrorResponse } from './error-response-schema';
import { LoginMethodDeniedError } from './error.login-method-denied';
import { PersonalEmailNotAllowedError } from './error.personal-email-not-allowed';
import { convertOrpcErrorToErrorResponse, isOrpcError } from './orpc/error-adapter';

/**
 * APIエラーの種類を表す列挙型
 */
export const ApiErrorType = {
  Validation: 'VALIDATION',
  Authentication: 'AUTHENTICATION',
  Authorization: 'AUTHORIZATION',
  NotFound: 'NOT_FOUND',
  System: 'SYSTEM',
  Conflict: 'CONFLICT'
} as const;
export type ApiErrorType = (typeof ApiErrorType)[keyof typeof ApiErrorType];
export const ERROR_MESSAGES = {
  UNEXPECTED: 'An unexpected error occurred',
  DEFAULT: 'An error occurred',
  VALIDATION_EN: 'Input content is invalid',
  VALIDATION_JA: '入力内容に不備があります'
} as const;

/**
 * APIエラーを扱うためのクラス
 */
export class ApiError extends Error {
  readonly response?: ErrorResponse;
  private _type: ApiErrorType;
  constructor(res: unknown) {
    super();
    const parsed = ErrorResponseSchema.safeParse(res);
    if (parsed.success) {
      this.response = parsed.data;
      this.cause = parsed.data.systemMessage;
      this._type = determineErrorType(parsed.data.statusCode);
    } else {
      this.message = ERROR_MESSAGES.UNEXPECTED;
      this._type = ApiErrorType.System;
    }
  }
  public get type(): ApiErrorType {
    return this._type;
  }

  /**
   * エラーレスポンスを取得（存在しない場合はデフォルト値を返す）
   */
  getResponse(): ErrorResponse {
    return this.response ?? {
      statusCode: HTTP_STATUS.INTERNAL_SERVER_ERROR,
      displayMessages: {},
      systemMessage: [ERROR_MESSAGES.UNEXPECTED]
    };
  }

  /**
   * エラー種類を判定するヘルパーメソッド
   */
  is(type: ApiErrorType): boolean {
    return this.type === type;
  }
}

/**
 * APIレスポンスからデータを取得するか、エラーを投げる
 */
export function getDataOrThrowApiError<T>({
  data,
  error
}: {
  data?: T;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error?: any;
}): T {
  // エラーが存在する場合は常にエラーを投げる
  if (error) {
    throw new ApiError(error);
  }

  // dataがundefinedでも、エラーがない場合は成功とみなす
  // (PATCH/DELETEなどでcontent: neverのケースに対応)
  return data as T;
}

/**
 * エラーメッセージを取得するためのハンドラー
 * @returns エラーメッセージと追加情報を含むオブジェクト
 */
export const handleApiError = (error: unknown, i18n: {
  language: string;
}): {
  message: string;
  type?: ApiErrorType;
  requiresAuth?: boolean;
} => {
  // 個人メアドログイン不可エラー（403）の場合
  if (error instanceof PersonalEmailNotAllowedError) {
    return {
      message: error.displayMessage ?? ERROR_MESSAGES.DEFAULT,
      type: ApiErrorType.Authorization
    };
  }

  // SSO 必須組織でのログイン方式拒否エラー（403）の場合。BE 多言語メッセージをそのまま返す。
  if (error instanceof LoginMethodDeniedError) {
    return {
      message: error.displayMessage ?? ERROR_MESSAGES.DEFAULT,
      type: ApiErrorType.Authorization
    };
  }

  // S3 presigned POST アップロード失敗（DC-7650 / DC-8388）
  if (error instanceof PresignedPostUploadError) {
    return {
      message: getPresignedPostUploadErrorMessage(error, i18n),
      type: ApiErrorType.System
    };
  }

  // エラーがApiErrorインスタンスの場合
  if (error instanceof ApiError) {
    const response = error.getResponse();
    const language = i18n.language;
    const message = extractErrorMessage(response, language);
    return {
      message,
      type: error.type,
      requiresAuth: error.is(ApiErrorType.Authentication)
    };
  }

  // oRPC クライアントが投げる ORPCError の場合
  if (isOrpcError(error)) {
    const response = convertOrpcErrorToErrorResponse(error);
    const errorType = determineErrorType(response.statusCode);
    const message = extractErrorMessage(response, i18n.language);
    return {
      message,
      type: errorType,
      requiresAuth: errorType === ApiErrorType.Authentication
    };
  }

  // エラーがErrorインスタンスでない場合
  if (!(error instanceof Error)) {
    return {
      message: ERROR_MESSAGES.DEFAULT,
      type: ApiErrorType.System
    };
  }

  // その他のエラーの場合
  return {
    message: error.message || ERROR_MESSAGES.DEFAULT,
    type: ApiErrorType.System
  };
};

/**
 * エラーの種類をステータスコードから判定
 */
const determineErrorType = (statusCode: number): ApiErrorType => {
  switch (true) {
    case statusCode === HTTP_STATUS.BAD_REQUEST:
      return ApiErrorType.Validation;
    case statusCode === HTTP_STATUS.UNAUTHORIZED:
      return ApiErrorType.Authentication;
    case statusCode === HTTP_STATUS.FORBIDDEN:
      return ApiErrorType.Authorization;
    case statusCode === HTTP_STATUS.NOT_FOUND:
      return ApiErrorType.NotFound;
    case statusCode === HTTP_STATUS.CONFLICT:
      return ApiErrorType.Conflict;
    default:
      return ApiErrorType.System;
  }
};

/**
 * エラーメッセージを抽出
 */
const extractErrorMessage = (response: ErrorResponse, locale: string = LANGUAGE_CONFIG['en-US'].code): string => {
  // バリデーションエラーかつvalidationMessagesが存在しない場合のデフォルトメッセージ
  if (determineErrorType(response.statusCode) === ApiErrorType.Validation && !response.validationMessages && !response.displayMessage && !response.displayMessages[locale]) {
    return locale === LANGUAGE_CONFIG['ja-JP'].code ? ERROR_MESSAGES.VALIDATION_JA : ERROR_MESSAGES.VALIDATION_EN;
  }

  // NOTE:メッセージの優先順位付きのフォールバックチェーン
  const messages = [
  // 1. バリデーションメッセージ（複数のメッセージをカンマ区切りで結合）
  response.validationMessages && Object.values(response.validationMessages).flat().join(', '),
  // 2. 指定されたロケールの表示用メッセージ
  response.displayMessage, response.displayMessages[locale],
  // 3. 英語のメッセージの表示用メッセージ
  response.displayMessages['en-US'],
  // 4. デフォルトメッセージ
  ERROR_MESSAGES.DEFAULT];
  const message = messages.find((msg): msg is string => typeof msg === 'string');
  return message ?? ERROR_MESSAGES.DEFAULT;
};